import React from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import { translateService, htmlHelperService } from "../../services/imports";
import SeenTracker, { qualifies } from "./seenTracker";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Viewer of docs/RUNBOOK-document-page.md §3.2 (DP2): one container per page
// from the start, react-pdf Pages only near the visible range, page-seen
// tracking. react-pdf 5.4.0 / pdfjs-dist 2.9.359.

// module level: a new object would make react-pdf reload the document
const PDF_OPTIONS = { isEvalSupported: false };

const PAD = 12; // around the page column, px
const GAP = 12; // between pages, px
const WINDOW = 2; // pages mounted before and after the visible range
const SIZE_BATCH = 20; // page sizes read per round trip
const TICK_MS = 250;
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
// iOS Safari leaves a larger canvas blank (§3.2 item 4)
const MAX_CANVAS_SIDE = 4096;
const MAX_CANVAS_PIXELS = 16 * 1024 * 1024;
const A4_RATIO = 297 / 210;
// zoom 100 % fits the page to the viewer width, but never wider than this:
// an A4 page 1300 px wide is two screens tall
const MAX_FIT_WIDTH = 1000;
const STORAGE_PREFIX = "documentViewer.seen:";

// the width react-pdf renders a page at; rounded down so the canvas never
// ends up a pixel over the limits above
const pageWidthFor = (width, zoom) => Math.max(Math.floor(width * zoom), 1);

export default class DocumentViewer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      status: "idle", // idle | loading | ready | error | password
      file: null,
      numPages: 0,
      sizes: [], // {w, h} at scale 1 per page, null until read
      width: 0, // fit width of a page, px
      zoom: 1,
      first: 0, // visible range, 0-based
      last: -1,
      current: 1,
      pageInput: null, // text while the page field is being edited
      seenCount: 0,
      allSeen: false,
    };

    this.rootRef = React.createRef();
    this.loadToken = 0;
    this.tracker = null;
    this.rendered = new Set(); // pages whose canvas is painted at the current scale
    this.proxies = {}; // pageNumber -> PDFPageProxy
    this.renderedHandlers = {};
    this.pageLoadHandlers = {};
    this.layoutCache = null;
    this.anchor = null;
    this.cancelSource = null;
  }

  componentDidMount() {
    const root = this.rootRef.current;
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(root);
    } else {
      window.addEventListener("resize", this.measure);
    }
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.measure();
    this.load();
  }

  // the page hides the viewer (display:none) in the same commit that pauses
  // it: read the position while the root still has a box
  getSnapshotBeforeUpdate(prevProps) {
    const root = this.rootRef.current;
    if (!prevProps.paused && this.props.paused && root && root.clientHeight) {
      return { top: root.scrollTop, left: root.scrollLeft, width: root.clientWidth };
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.revisionKey !== this.props.revisionKey) {
      this.load();
      return;
    }
    // the page passed a fresh URL (the pre-signed one had expired)
    if (this.state.status === "error" && prevProps.url !== this.props.url) {
      this.load();
      return;
    }

    if (prevProps.paused !== this.props.paused) this.onPausedChange(snapshot);

    const s = this.state;
    // react-pdf repaints only when the page's pixel width changes; then
    // onRenderSuccess comes again for every mounted page
    if (pageWidthFor(prevState.width, prevState.zoom) !== pageWidthFor(s.width, s.zoom)) {
      this.rendered.clear();
      if (this.tracker) this.addSeen(this.tracker.clearQualifying(Date.now()));
    }

    if (prevState.first !== s.first || prevState.last !== s.last) {
      this.releaseLeftPages(prevState.first, prevState.last);
    }

    const layoutChanged =
      prevState.width !== s.width ||
      prevState.zoom !== s.zoom ||
      prevState.sizes !== s.sizes ||
      prevState.numPages !== s.numPages;
    if (layoutChanged) {
      this.applyAnchor();
      this.updateVisibility();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.loadToken++;
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener("resize", this.measure);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.cancelSource) this.cancelSource.cancel();
    this.stopTicking();
  }

  // ---- loading -----------------------------------------------------------

  load = (url = this.props.url) => {
    // a download still running for an earlier revision is not needed
    if (this.cancelSource) this.cancelSource.cancel();
    this.cancelSource = null;
    const token = ++this.loadToken;
    this.stopTicking();
    this.tracker = null;
    this.rendered.clear();
    this.proxies = {};
    this.layoutCache = null;
    this.anchor = null;

    if (!url) {
      this.setState({ status: "idle", file: null, numPages: 0, sizes: [] });
      return;
    }

    this.setState({
      status: "loading",
      file: null,
      numPages: 0,
      sizes: [],
      first: 0,
      last: -1,
      current: 1,
      seenCount: 0,
      allSeen: false,
    });
    const root = this.rootRef.current;
    if (root) root.scrollTop = 0;

    const source = axios.CancelToken.source();
    this.cancelSource = source;
    htmlHelperService.getPdfBytes(url, true, source.token).then(
      (bytes) => {
        if (this.cancelSource === source) this.cancelSource = null;
        if (token !== this.loadToken) return;
        if (this.props.onBytes) this.props.onBytes(bytes, this.props.revisionKey);
        // the same ArrayBuffer object on every render; pdf.js copies it
        this.setState({ file: bytes });
      },
      () => {
        if (this.cancelSource === source) this.cancelSource = null;
        if (token !== this.loadToken) return;
        this.setState({ status: "error" });
      }
    );
  };

  // Try again: an expired pre-signed URL is replaced through the page
  // (refreshUrl reloads the document result), not guessed
  retry = () => {
    const root = this.rootRef.current;
    // the button goes away with the message; keep keyboard focus in the viewer
    if (root) root.focus();
    const { refreshUrl } = this.props;
    const key = this.props.revisionKey;
    this.setState({ status: "loading" });
    const next = refreshUrl
      ? refreshUrl().catch(() => this.props.url)
      : Promise.resolve(this.props.url);
    next.then((url) => {
      // a new revision key has already started its own load
      if (!this.unmounted && this.props.revisionKey === key) this.load(url || this.props.url);
    });
  };

  onDocumentLoad = (pdf) => {
    const token = this.loadToken;
    const numPages = pdf.numPages;
    this.tracker = new SeenTracker(numPages, this.restoreSeen(numPages));

    this.setState(
      {
        status: "ready",
        numPages,
        sizes: new Array(numPages).fill(null),
        seenCount: this.countSeen(),
        allSeen: this.tracker.allSeen,
      },
      () => {
        this.reportProgress();
        this.loadSizes(pdf, 0, token);
      }
    );
  };

  onDocumentError = () => {
    this.setState({ status: "error" });
  };

  // react-pdf's default asks with window.prompt() and asks again on Cancel,
  // for ever; unmounting the Document ends the password request instead
  onDocumentPassword = () => {
    if (!this.unmounted) this.setState({ status: "password" });
  };

  // page sizes at scale 1 (page dictionary only, no content), in batches
  loadSizes(pdf, start, token) {
    const numPages = pdf.numPages;
    if (start >= numPages || token !== this.loadToken || this.unmounted) return;

    const end = Math.min(numPages, start + SIZE_BATCH);
    const batch = [];
    for (let i = start; i < end; i++) {
      batch.push(
        pdf.getPage(i + 1).then(
          (page) => {
            this.proxies[i + 1] = page;
            const vp = page.getViewport({ scale: 1 });
            return { w: vp.width, h: vp.height };
          },
          () => null
        )
      );
    }

    Promise.all(batch).then((sizes) => {
      if (token !== this.loadToken || this.unmounted) return;
      this.captureAnchor();
      this.setState(
        (prev) => {
          const next = prev.sizes.slice();
          sizes.forEach((size, k) => (next[start + k] = size));
          return { sizes: next };
        },
        () => this.loadSizes(pdf, end, token)
      );
    });
  }

  // ---- layout ------------------------------------------------------------

  measure = () => {
    const root = this.rootRef.current;
    if (!root) return;
    // hidden (popup behind Contact/Terms): keep the last width, render nothing new
    const clientWidth = Math.floor(root.clientWidth);
    if (!clientWidth) {
      this.updateVisibility();
      return;
    }
    const width = Math.max(Math.min(clientWidth - 2 * PAD, MAX_FIT_WIDTH), 1);
    if (width !== this.state.width) {
      this.captureAnchor();
      this.setState({ width, zoom: Math.min(this.state.zoom, this.maxZoom(width)) });
    } else {
      // a height-only resize changes the visible range and the 0.9 x viewer
      // height threshold without a scroll event
      this.updateVisibility();
    }
  };

  getLayout() {
    const { numPages, sizes, width, zoom } = this.state;
    const cache = this.layoutCache;
    if (
      cache &&
      cache.numPages === numPages &&
      cache.sizes === sizes &&
      cache.width === width &&
      cache.zoom === zoom
    ) {
      return cache;
    }

    const pageWidth = pageWidthFor(width, zoom);
    const firstRatio = sizes[0] ? sizes[0].h / sizes[0].w : A4_RATIO;
    const heights = [];
    const tops = [];
    let y = PAD;
    for (let i = 0; i < numPages; i++) {
      const ratio = sizes[i] ? sizes[i].h / sizes[i].w : firstRatio;
      const height = Math.round(pageWidth * ratio);
      tops.push(y);
      heights.push(height);
      y += height + GAP;
    }

    this.layoutCache = {
      numPages,
      sizes,
      width,
      zoom,
      pageWidth,
      heights,
      tops,
      left: PAD + Math.max(0, Math.round((width - pageWidth) / 2)),
      innerWidth: Math.max(width, pageWidth) + 2 * PAD,
      total: numPages ? y - GAP + PAD : 0,
    };
    return this.layoutCache;
  }

  // index of the page at vertical offset y (the gap belongs to the page above)
  pageIndexAt(layout, y) {
    const { tops } = layout;
    let lo = 0;
    let hi = tops.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (tops[mid] <= y) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  // the page at a scroll position, kept in place across a layout change
  anchorAt(top, left, clientWidth) {
    const layout = this.getLayout();
    if (!layout.numPages) return null;
    const index = this.pageIndexAt(layout, top);
    return {
      index,
      fraction: (top - layout.tops[index]) / (layout.heights[index] || 1),
      centerX: (left + clientWidth / 2) / (layout.innerWidth || 1),
    };
  }

  captureAnchor() {
    const root = this.rootRef.current;
    // a hidden root reads 0: keep the anchor taken before it was hidden
    if (!root || !root.clientHeight || this.anchor) return;
    this.anchor = this.anchorAt(root.scrollTop, root.scrollLeft, root.clientWidth);
  }

  applyAnchor() {
    const root = this.rootRef.current;
    const anchor = this.anchor;
    // setting scrollTop on an element without a box does nothing: wait
    if (!root || !anchor || !root.clientHeight) return;
    this.anchor = null;
    const layout = this.getLayout();
    if (!layout.numPages || anchor.index >= layout.numPages) return;
    root.scrollTop = Math.round(
      layout.tops[anchor.index] + anchor.fraction * layout.heights[anchor.index]
    );
    root.scrollLeft = Math.max(
      0,
      Math.round(anchor.centerX * layout.innerWidth - root.clientWidth / 2)
    );
  }

  // ---- visibility and tracking -------------------------------------------

  onScroll = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateVisibility();
    });
  };

  canTrack() {
    const root = this.rootRef.current;
    return (
      !!root &&
      !this.props.paused &&
      document.visibilityState === "visible" &&
      root.clientHeight > 0
    );
  }

  updateVisibility = () => {
    const root = this.rootRef.current;
    const layout = this.getLayout();
    if (!root || !layout.numPages || !this.state.width) return;

    const viewTop = root.scrollTop;
    const viewHeight = root.clientHeight;
    if (!viewHeight) {
      this.stopTracking();
      return;
    }
    const viewBottom = viewTop + viewHeight;

    const first = this.pageIndexAt(layout, viewTop);
    let last = first;
    while (last + 1 < layout.numPages && layout.tops[last + 1] < viewBottom) last++;

    const now = Date.now();
    const track = this.canTrack() && this.tracker;
    let current = first;
    let bestVisible = -1;
    const seen = [];

    if (this.tracker) {
      // pages that were qualifying but are no longer in view
      for (const page of Array.from(this.tracker.qualifying)) {
        const i = page - 1;
        if (i < first || i > last || !track) {
          seen.push(...this.tracker.setQualifying(page, false, now));
        }
      }
    }

    for (let i = first; i <= last; i++) {
      const top = layout.tops[i];
      const bottom = top + layout.heights[i];
      const visible = Math.max(0, Math.min(bottom, viewBottom) - Math.max(top, viewTop));
      if (visible > bestVisible) {
        bestVisible = visible;
        current = i;
      }
      if (this.tracker) {
        const q =
          track &&
          this.rendered.has(i + 1) &&
          qualifies(visible, layout.heights[i], viewHeight);
        seen.push(...this.tracker.setQualifying(i + 1, q, now));
      }
    }

    this.addSeen(seen);
    if (track && this.tracker.active) this.startTicking();
    else if (this.tracker && !this.tracker.active) this.stopTicking();

    const s = this.state;
    if (s.first !== first || s.last !== last || s.current !== current + 1) {
      this.setState({ first, last, current: current + 1 });
    }
  };

  startTicking() {
    if (this.timer) return;
    this.timer = setInterval(() => {
      if (!this.tracker) return this.stopTicking();
      this.addSeen(this.tracker.tick(Date.now()));
      if (!this.tracker.active) this.stopTicking();
    }, TICK_MS);
  }

  stopTicking() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  stopTracking() {
    if (this.tracker) {
      this.addSeen(this.tracker.clearQualifying(Date.now()));
      this.tracker.pause(Date.now());
    }
    this.stopTicking();
  }

  onPausedChange(snapshot) {
    const root = this.rootRef.current;
    if (this.props.paused) {
      // hidden: some engines reset the scroll position of a display:none
      // element, so keep it as a page anchor and apply it when shown
      if (snapshot && root && !root.clientHeight) {
        this.anchor = this.anchorAt(snapshot.top, snapshot.left, snapshot.width);
      }
      this.stopTracking();
      return;
    }
    this.applyAnchor();
    this.measure();
    this.updateVisibility();
  }

  onVisibilityChange = () => {
    if (document.visibilityState === "visible") this.updateVisibility();
    else this.stopTracking();
  };

  onPageRendered(page) {
    this.rendered.add(page);
    this.updateVisibility();
  }

  getRenderedHandler(page) {
    if (!this.renderedHandlers[page]) {
      this.renderedHandlers[page] = () => this.onPageRendered(page);
    }
    return this.renderedHandlers[page];
  }

  getPageLoadHandler(page) {
    if (!this.pageLoadHandlers[page]) {
      this.pageLoadHandlers[page] = (proxy) => (this.proxies[page] = proxy);
    }
    return this.pageLoadHandlers[page];
  }

  // react-pdf 5.4.0 zeroes the canvas on unmount but never calls
  // page.cleanup(), so pdf.js would keep each page's operator list and
  // decoded images for the life of the document (§3.2 item 3)
  releaseLeftPages(prevFirst, prevLast) {
    const { first, last } = this.state;
    const from = Math.max(0, prevFirst - WINDOW);
    const to = prevLast + WINDOW;
    for (let i = from; i <= to; i++) {
      if (i >= first - WINDOW && i <= last + WINDOW) continue;
      const page = i + 1;
      this.rendered.delete(page);
      const proxy = this.proxies[page];
      if (proxy) {
        try {
          proxy.cleanup();
        } catch (e) {
          // a page of a destroyed document
        }
      }
    }
  }

  addSeen(pages) {
    if (!pages || !pages.length || !this.tracker) return;
    this.persistSeen();
    this.setState({ seenCount: this.countSeen(), allSeen: this.tracker.allSeen });
    this.reportProgress();
  }

  countSeen() {
    return this.tracker ? this.tracker.pages.filter((p) => p.firstSeenAt).length : 0;
  }

  reportProgress() {
    if (this.props.onProgress && this.tracker) {
      this.props.onProgress(this.tracker.snapshot());
    }
  }

  // progress per browser, keyed by revision and reader (§3.2 "Output");
  // only when the page passes a storageKey
  restoreSeen(numPages) {
    const key = this.props.storageKey;
    if (!key) return null;
    try {
      const data = JSON.parse(window.localStorage.getItem(STORAGE_PREFIX + key));
      return data && data.pageCount === numPages ? data.seen : null;
    } catch (e) {
      return null;
    }
  }

  persistSeen() {
    const key = this.props.storageKey;
    if (!key || !this.tracker) return;
    try {
      window.localStorage.setItem(
        STORAGE_PREFIX + key,
        JSON.stringify({ pageCount: this.tracker.pageCount, seen: this.tracker.toStorage() })
      );
    } catch (e) {
      // storage full or blocked: progress is a convenience
    }
  }

  // ---- toolbar -----------------------------------------------------------

  maxZoom(width = this.state.width) {
    const { sizes } = this.state;
    if (!width) return 1;
    const dpr = window.devicePixelRatio || 1;
    let ratio = A4_RATIO;
    for (const size of sizes) if (size && size.h / size.w > ratio) ratio = size.h / size.w;
    const bySide = MAX_CANVAS_SIDE / (dpr * width);
    const byPixels = Math.sqrt(MAX_CANVAS_PIXELS / ratio) / (dpr * width);
    return Math.max(ZOOM_STEPS[0], Math.min(ZOOM_STEPS[ZOOM_STEPS.length - 1], bySide, byPixels));
  }

  setZoom(zoom) {
    if (zoom === this.state.zoom) return;
    this.captureAnchor();
    this.setState({ zoom });
  }

  zoomIn = () => {
    const max = this.maxZoom();
    const next = ZOOM_STEPS.find((z) => z > this.state.zoom + 0.001);
    this.setZoom(Math.min(next || max, max));
  };

  zoomOut = () => {
    const smaller = ZOOM_STEPS.filter((z) => z < this.state.zoom - 0.001);
    if (smaller.length) this.setZoom(smaller[smaller.length - 1]);
  };

  resetZoom = () => this.setZoom(1);

  goToPage(page) {
    const root = this.rootRef.current;
    const layout = this.getLayout();
    if (!root || !layout.numPages) return;
    const index = Math.min(Math.max(page, 1), layout.numPages) - 1;
    root.scrollTop = Math.max(0, layout.tops[index] - GAP / 2);
    this.updateVisibility();
  }

  // Enter keeps the field focused: show the page reached, selected, so the
  // next number replaces it instead of being appended
  onPageSubmit = (e) => {
    e.preventDefault();
    const input = e.currentTarget.querySelector("input");
    const page = parseInt(this.state.pageInput, 10);
    const { numPages, current } = this.state;
    const shown = isNaN(page) || !numPages ? current : Math.min(Math.max(page, 1), numPages);
    if (!isNaN(page)) this.goToPage(page);
    this.setState({ pageInput: String(shown) }, () => input && input.select());
  };

  // ---- render ------------------------------------------------------------

  renderPage(index, layout) {
    const page = index + 1;
    const { first, last, numPages } = this.state;
    const mounted = index >= first - WINDOW && index <= last + WINDOW;
    return (
      <div
        key={page}
        className="document-viewer__page"
        role="group"
        aria-label={translateService.t("document.pageLabel", page, numPages)}
        style={{
          top: layout.tops[index],
          left: layout.left,
          width: layout.pageWidth,
          height: layout.heights[index],
        }}
      >
        {mounted ? (
          <Page
            pageNumber={page}
            width={layout.pageWidth}
            renderTextLayer={true}
            renderAnnotationLayer={false}
            loading=""
            onLoadSuccess={this.getPageLoadHandler(page)}
            onRenderSuccess={this.getRenderedHandler(page)}
          />
        ) : (
          <span className="document-viewer__placeholder" aria-hidden="true">
            {page}
          </span>
        )}
      </div>
    );
  }

  renderToolbar() {
    const { numPages, current, zoom, pageInput, status } = this.state;
    const disabled = status !== "ready";
    const max = this.maxZoom();
    return (
      <div
        className="document-viewer__toolbar"
        role="toolbar"
        aria-label={translateService.t("document.viewerToolbar")}
      >
        <form className="document-viewer__pages-field" onSubmit={this.onPageSubmit}>
          <label className="sr-only" htmlFor="document-viewer-page">
            {translateService.t("document.goToPage")}
          </label>
          <input
            id="document-viewer-page"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            value={pageInput !== null ? pageInput : numPages ? String(current) : ""}
            onFocus={(e) => {
              this.setState({ pageInput: String(current) });
              e.target.select();
            }}
            onChange={(e) => this.setState({ pageInput: e.target.value.replace(/\D/g, "") })}
            onBlur={() => this.setState({ pageInput: null })}
          />
          <span className="document-viewer__page-count">/ {numPages || "–"}</span>
        </form>
        <div className="document-viewer__zoom">
          <button
            type="button"
            onClick={this.zoomOut}
            disabled={disabled || zoom <= ZOOM_STEPS[0] + 0.001}
            aria-label={translateService.t("document.zoomOut")}
            title={translateService.t("document.zoomOut")}
          >
            −
          </button>
          <span className="document-viewer__zoom-value" aria-live="polite">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={this.zoomIn}
            disabled={disabled || zoom >= max - 0.001}
            aria-label={translateService.t("document.zoomIn")}
            title={translateService.t("document.zoomIn")}
          >
            +
          </button>
          <button
            type="button"
            className="document-viewer__fit"
            onClick={this.resetZoom}
            disabled={disabled || zoom === 1}
            aria-label={translateService.t("document.resetZoom")}
            title={translateService.t("document.resetZoom")}
          >
            ↔
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { status, file, numPages, width, seenCount, allSeen } = this.state;
    const layout = this.getLayout();
    const pages = [];
    if (width > 0) for (let i = 0; i < numPages; i++) pages.push(this.renderPage(i, layout));

    return (
      <div className="document-viewer">
        {this.renderToolbar()}
        <div
          ref={this.rootRef}
          className="document-viewer__scroll"
          role="region"
          tabIndex={0}
          onScroll={this.onScroll}
          aria-label={translateService.t("document.viewer")}
          aria-busy={status === "loading"}
          data-page-count={numPages}
          data-seen-pages={seenCount}
          data-all-seen={allSeen}
        >
          {status === "loading" && (
            <div className="document-viewer__message">
              {translateService.t("document.loading")}
            </div>
          )}
          {status === "error" && (
            <div className="document-viewer__message is-error" role="alert">
              <span>{translateService.t("document.loadError")}</span>
              <button type="button" onClick={this.retry}>
                {translateService.t("document.retry")}
              </button>
            </div>
          )}
          {status === "password" && (
            <div className="document-viewer__message is-error" role="alert">
              {translateService.t("document.passwordProtected")}
            </div>
          )}
          {file && (status === "loading" || status === "ready") && (
            <Document
              file={file}
              options={PDF_OPTIONS}
              onLoadSuccess={this.onDocumentLoad}
              onLoadError={this.onDocumentError}
              onPassword={this.onDocumentPassword}
              loading=""
              error=""
            >
              <div
                className="document-viewer__pages"
                style={{ height: layout.total, width: layout.innerWidth }}
              >
                {pages}
              </div>
            </Document>
          )}
        </div>
      </div>
    );
  }
}
